// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/MissionControlHeader.jsx

import React from "react";
import OperationalCaptureAction from "../operational-capture/OperationalCaptureAction";
import { Button, StatusChip } from "../../design-system/components";

function formatScope(framework) {
  return framework && framework !== "all" ? framework : "All frameworks";
}

function getPostureFreshness(dataAsOf) {
  if (!dataAsOf) {
    return {
      label: "Unknown freshness",
      tone: "neutral",
      detail: "Awaiting current posture data",
    };
  }

  const timestamp = new Date(dataAsOf).getTime();
  if (Number.isNaN(timestamp)) {
    return {
      label: "Unknown freshness",
      tone: "neutral",
      detail: "Posture timestamp unavailable",
    };
  }

  const ageMs = Math.max(0, Date.now() - timestamp);
  const ageHours = ageMs / 3600000;
  const ageDays = Math.floor(ageHours / 24);

  if (ageHours < 24) {
    return {
      label: "Current",
      tone: "success",
      detail: `Updated ${new Date(dataAsOf).toLocaleString()}`,
    };
  }

  if (ageDays <= 7) {
    return {
      label: "Aging",
      tone: "warning",
      detail: `Posture data is ${ageDays} day${ageDays === 1 ? "" : "s"} old`,
    };
  }

  return {
    label: "Stale",
    tone: "warning",
    detail: `Posture data is ${ageDays} days old`,
  };
}

export default function MissionControlHeader({
  framework = "all",
  dataAsOf,
  warningCount = 0,
  refreshing = false,
  refreshMessage = "",
  refreshError = "",
  onRefresh,
  onOpenPosture,
  captureProps = null,
}) {
  const freshness = getPostureFreshness(dataAsOf);

  return (
    <header className="mission-control-header">
      <div className="mission-control-header__content">
        <h1>Mission Control</h1>
        <p>
          Understand current posture, identify what needs attention, and move
          directly into governed operational work.
        </p>

        <div className="mission-control-header__context">
          <StatusChip tone="informational">
            {formatScope(framework)}
          </StatusChip>
          <span>{freshness.detail}</span>
          <StatusChip tone={freshness.tone} dot>
            {freshness.label}
          </StatusChip>
          {warningCount > 0 ? (
            <StatusChip tone="warning">
              {warningCount} partial-data warning{warningCount === 1 ? "" : "s"}
            </StatusChip>
          ) : null}
        </div>

        {refreshMessage || refreshError ? (
          <div
            className={`mission-control-header__refresh-result ${
              refreshError
                ? "mission-control-header__refresh-result--error"
                : ""
            }`}
            role={refreshError ? "alert" : "status"}
          >
            {refreshError || refreshMessage}
          </div>
        ) : null}
      </div>

      <div
        className="mission-control-header__actions"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          columnGap: 16,
          rowGap: 10,
          marginLeft: "auto",
        }}
      >
        {captureProps ? (
          <OperationalCaptureAction {...captureProps} renderButton={({ onClick, disabled, label }) => (
            <Button variant="primary" size="medium" onClick={onClick} disabled={disabled} className="mission-control-header__capture-button">
              {label}
            </Button>
          )} />
        ) : null}
        <Button
          variant="secondary"
          size="medium"
          onClick={onRefresh}
          disabled={refreshing}
          className="mission-control-header__refresh-button"
        >
          {refreshing ? "Refreshing…" : "Refresh view"}
        </Button>
        <Button
          variant="secondary"
          size="medium"
          onClick={onOpenPosture}
          className="mission-control-header__posture-button"
        >
          Open Posture
        </Button>
      </div>
    </header>
  );
}
